import { type ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import useSWR from 'swr';
import { getFeedbackAnalytics, type AnalyticsRange, type AnalyticsFilters } from '@/services/api';
import type { VoteKind } from '@/lib/vote';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { FeedbackAnalytics } from '@/types/feedback';
import { KpiStrip } from './KpiStrip';
import { PriorityMap } from './PriorityMap';
import { NeedsAttention } from './NeedsAttention';
import { FeatureLeaderboard } from './FeatureLeaderboard';
import { RatingHistogram } from './RatingHistogram';

interface FeedbackDashboardProps {
  accountToken: string;
  range: AnalyticsRange;
  /** Resolved current window (yyyy-MM-dd), used to derive the prior comparison period. */
  startDate: string;
  endDate: string;
  /** Content filters that also scope the charts/cards. */
  filters?: AnalyticsFilters;
  onFeatureSelect?: (feature: string) => void;
  onSentimentSelect?: (kind: VoteKind) => void;
}

// The equally-long window immediately preceding [startDate, endDate].
function previousWindow(startDate: string, endDate: string): { from: string; to: string } {
  const dayMs = 86_400_000;
  const start = new Date(startDate + 'T00:00:00Z').getTime();
  const end = new Date(endDate + 'T00:00:00Z').getTime();
  const prevEnd = start - dayMs;
  const prevStart = prevEnd - (end - start);
  const fmt = (ms: number) => new Date(ms).toISOString().slice(0, 10);
  return { from: fmt(prevStart), to: fmt(prevEnd) };
}

export function FeedbackDashboard({
  accountToken,
  range,
  startDate,
  endDate,
  filters,
  onFeatureSelect,
  onSentimentSelect,
}: FeedbackDashboardProps) {
  const { instance } = useMsal();

  const rangeKey = 'days' in range ? `d${range.days}` : `${range.from}_${range.to}`;
  const filterKey = filters
    ? `${filters.feature ?? ''}|${filters.customer ?? ''}|${filters.user ?? ''}|${filters.vote ?? ''}|${filters.rating ?? ''}`
    : '';
  const { data: analytics, error, isLoading } = useSWR<FeedbackAnalytics>(
    accountToken ? `analytics-${accountToken}-${rangeKey}-${filterKey}` : null,
    () => getFeedbackAnalytics(instance, accountToken, range, filters)
  );

  // Prior period (same length, same filters) for period-over-period deltas.
  // Only fetched once the current period has data to compare against.
  const prev = previousWindow(startDate, endDate);
  const { data: prevAnalytics } = useSWR<FeedbackAnalytics>(
    accountToken && analytics && analytics.totalFeedback > 0
      ? `analytics-prev-${accountToken}-${prev.from}_${prev.to}-${filterKey}`
      : null,
    () => getFeedbackAnalytics(instance, accountToken, prev, filters)
  );

  let content: ReactNode;

  if (isLoading) {
    content = (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        {/* Hero row: priority map (wider) + needs attention */}
        <div className="grid gap-4 md:grid-cols-[1.85fr_1fr]">
          <Skeleton className="h-[320px]" />
          <Skeleton className="h-[320px]" />
        </div>
        {/* Feature leaderboard */}
        <Skeleton className="h-64" />
        {/* Rating distribution */}
        <Skeleton className="h-48" />
      </div>
    );
  } else if (error) {
    content = (
      <Alert variant="destructive">
        <AlertDescription>Failed to load analytics. The feedback table will still load below.</AlertDescription>
      </Alert>
    );
  } else if (!analytics || analytics.totalFeedback === 0) {
    content = (
      <div className="rounded-md border bg-muted/50 py-6 text-center text-muted-foreground">
        No feedback in the selected period
      </div>
    );
  } else {
    const histogramScope = filters?.feature ? `Feature: ${filters.feature}` : 'All features';
    content = (
      <div className="space-y-4">
        {/* KPI strip */}
        <KpiStrip analytics={analytics} prevAnalytics={prevAnalytics} />

        {/* Legend for the KPI delta badges */}
        <p className="text-sm text-muted-foreground">
          Comparing to the previous period — deltas show the change in that window.
        </p>

        {/* Hero row: priority map (wider) + needs attention.
            minmax(0,…) tracks (not bare fr, which resolve to minmax(auto,…)) let the
            chart cell shrink below its content's min-content, so the Recharts
            ResponsiveContainer can never ratchet the layout wider than the viewport. */}
        <div className="grid gap-4 md:grid-cols-[minmax(0,1.85fr)_minmax(0,1fr)]">
          <PriorityMap features={analytics.perFeatureDaily} onFeatureSelect={onFeatureSelect} />
          <NeedsAttention features={analytics.perFeatureDaily} onFeatureSelect={onFeatureSelect} />
        </div>

        {/* Feature leaderboard */}
        <FeatureLeaderboard
          features={analytics.perFeatureDaily}
          onFeatureSelect={onFeatureSelect}
          onSentimentSelect={onSentimentSelect}
        />

        {/* Rating distribution */}
        <RatingHistogram distribution={analytics.ratingDistribution} scopeLabel={histogramScope} />
      </div>
    );
  }

  return <div className="space-y-4">{content}</div>;
}
